if (!customElements.get('gift-circle')) {
  customElements.define(
    'gift-circle',
    class GiftCircle extends HTMLElement {
      constructor() {
        super();
        this.cartTotal = Number(this.dataset.cartTotal || 0);
        this.currency = this.dataset.currency || 'QAR';
        this.moneyTemplate = this.dataset.moneyTemplate || `${this.currency} [amount]`;
        this.shopMoreTemplate = this.parseTemplate(
          this.dataset.shopMoreTemplate,
          'Shop for <strong>[amount]</strong> more'
        );
        this.earnedTemplate = this.parseTemplate(
          this.dataset.earnedTemplate,
          '<span class="gift-circle__earned-icon" aria-hidden="true">✓</span> You\'ve earned a [gift]'
        );
        this.openButton = this.querySelector('[data-gift-circle-open]');
        this.closeButtons = this.querySelectorAll('[data-gift-circle-close]');
        this.panel = this.querySelector('.gift-circle__panel');
        this.tiers = Array.from(this.querySelectorAll('[data-gift-tier]'));
        this.productScrollers = [];
        this.productIntervals = [];
        this.isInitialized = false;
        this.onKeyUp = this.onKeyUp.bind(this);
        this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
      }

      connectedCallback() {
        if (!this.isInitialized) {
          this.openButton?.addEventListener('click', () => this.toggle());

          this.closeButtons.forEach((button) => {
            button.addEventListener('click', () => this.close());
          });

          this.querySelectorAll('[data-gift-products]').forEach((scroller) => {
            const tier = scroller.closest('[data-gift-tier]');
            const prev = tier?.querySelector('[data-gift-prev]');
            const next = tier?.querySelector('[data-gift-next]');

            if (scroller.children.length > 1) this.productScrollers.push(scroller);

            prev?.addEventListener('click', () => {
              this.scrollProducts(scroller, -1);
              this.restartAutoSwitch();
            });

            next?.addEventListener('click', () => {
              this.scrollProducts(scroller, 1);
              this.restartAutoSwitch();
            });
          });

          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
            subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
              this.refreshCartTotal();
            });
          }

          this.isInitialized = true;
        }

        this.updateProgress();
      }

      disconnectedCallback() {
        this.stopAutoSwitch();
        document.removeEventListener('keyup', this.onKeyUp);
        document.removeEventListener('pointerdown', this.onDocumentPointerDown);
      }

      toggle() {
        if (this.classList.contains('is-open')) {
          this.close();
        } else {
          this.open();
        }
      }

      open() {
        this.classList.add('is-open');
        this.openButton?.setAttribute('aria-expanded', 'true');
        this.panel?.setAttribute('aria-hidden', 'false');
        document.addEventListener('keyup', this.onKeyUp);
        window.setTimeout(() => {
          document.addEventListener('pointerdown', this.onDocumentPointerDown);
        }, 0);
        this.refreshCartTotal();
        this.startAutoSwitch();
        window.setTimeout(() => {
          this.querySelector('.gift-circle__close')?.focus();
        }, 0);
      }

      close() {
        this.classList.remove('is-open');
        this.openButton?.setAttribute('aria-expanded', 'false');
        this.panel?.setAttribute('aria-hidden', 'true');
        document.removeEventListener('keyup', this.onKeyUp);
        document.removeEventListener('pointerdown', this.onDocumentPointerDown);
        this.stopAutoSwitch();
        this.openButton?.focus();
      }

      onKeyUp(event) {
        if (event.code === 'Escape') this.close();
      }

      onDocumentPointerDown(event) {
        if (!this.classList.contains('is-open')) return;
        if (this.contains(event.target)) return;
        this.close();
      }

      startAutoSwitch() {
        this.stopAutoSwitch();

        this.productIntervals = this.productScrollers.map((scroller) =>
          window.setInterval(() => {
            this.scrollProducts(scroller, 1);
          }, 5000)
        );
      }

      stopAutoSwitch() {
        this.productIntervals.forEach((interval) => window.clearInterval(interval));
        this.productIntervals = [];
      }

      restartAutoSwitch() {
        if (!this.classList.contains('is-open')) return;
        this.startAutoSwitch();
      }

      scrollProducts(scroller, direction) {
        const items = Array.from(scroller.children);
        if (items.length <= 1) return;

        const currentIndex = items.reduce((closestIndex, item, index) => {
          const closestDistance = Math.abs(items[closestIndex].offsetLeft - scroller.scrollLeft);
          const itemDistance = Math.abs(item.offsetLeft - scroller.scrollLeft);

          return itemDistance < closestDistance ? index : closestIndex;
        }, 0);
        const nextIndex = (currentIndex + direction + items.length) % items.length;

        scroller.scrollTo({
          left: items[nextIndex].offsetLeft,
          behavior: 'smooth',
        });
      }

      refreshCartTotal() {
        const cartUrl = window.routes?.cart_url ? `${window.routes.cart_url}.js` : '/cart.js';

        fetch(cartUrl, { cache: 'no-store' })
          .then((response) => {
            if (!response.ok) throw new Error('Cart request failed');
            return response.json();
          })
          .then((cart) => {
            if (typeof cart.total_price === 'number') {
              this.cartTotal = cart.total_price;
              this.updateProgress();
            }
          })
          .catch(() => {});
      }

      updateProgress() {
        this.tiers.forEach((tier) => {
          const threshold = Number(tier.dataset.threshold || 0);
          const progress = threshold > 0 ? Math.min(100, Math.round((this.cartTotal / threshold) * 100)) : 0;
          const progressBar = tier.querySelector('[data-gift-progress]');
          const title = tier.querySelector('.gift-circle__tier-title');
          const message = tier.querySelector('[data-gift-message]');
          const giftName = tier.dataset.giftName || 'FREE gift';
          const remaining = Math.max(0, threshold - this.cartTotal);
          const isUnlocked = this.cartTotal >= threshold;

          tier.classList.toggle('is-unlocked', isUnlocked);
          if (progressBar) progressBar.style.width = `${progress}%`;
          if (title && !title.dataset.defaultText) title.dataset.defaultText = title.textContent;
          if (title) {
            title.innerHTML = isUnlocked
              ? this.earnedTemplate.replace('[gift]', giftName)
              : title.dataset.defaultText;
          }
          if (message) {
            message.hidden = isUnlocked;
            if (!isUnlocked) message.innerHTML = this.shopMoreTemplate.replace('[amount]', this.formatMoney(remaining));
          }
        });
      }

      formatMoney(amount) {
        const value = Math.ceil(amount / 100);
        return this.moneyTemplate.replace('[amount]', value);
      }

      parseTemplate(value, fallback) {
        if (!value) return fallback;

        try {
          return JSON.parse(value);
        } catch (error) {
          return value;
        }
      }
    }
  );
}
